import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

/**
 * Builds a compact page-number sequence with ellipses, e.g.:
 *   1 2 3 4 5           (few pages — show them all)
 *   1 2 3 4 5 … 12       (near the start)
 *   1 … 8 9 10 11 12     (near the end)
 *   1 … 5 6 7 … 12       (somewhere in the middle)
 */
function buildPageList(page: number, totalPages: number): (number | 'ellipsis')[] {
  const SIBLINGS = 1;
  const totalSlots = SIBLINGS * 2 + 5; // first, last, current, 2 siblings, 2 ellipses

  if (totalPages <= totalSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const left = Math.max(page - SIBLINGS, 1);
  const right = Math.min(page + SIBLINGS, totalPages);

  const pages: (number | 'ellipsis')[] = [1];

  if (left > 2) pages.push('ellipsis');
  for (let p = Math.max(left, 2); p <= Math.min(right, totalPages - 1); p++) pages.push(p);
  if (right < totalPages - 1) pages.push('ellipsis');

  pages.push(totalPages);
  return pages;
}

export default function Pagination({
  page,
  totalPages,
  onChange,
  totalItems,
  pageSize,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
}) {
  if (totalPages <= 1) return null;

  const from = totalItems && pageSize ? (page - 1) * pageSize + 1 : null;
  const to = totalItems && pageSize ? Math.min(page * pageSize, totalItems) : null;
  const pages = buildPageList(page, totalPages);

  const navBtn = 'w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0';

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap px-1 pt-4 mt-1 border-t border-gray-50">
      <p className="text-[12px] text-gray-500 font-medium whitespace-nowrap">
        {from && to && totalItems ? `Showing ${from}–${to} of ${totalItems}` : `Page ${page} of ${totalPages}`}
      </p>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page === 1}
          aria-label="Previous page"
          className={navBtn}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-[12px]" />
        </button>

        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-gray-300 text-[13px] font-bold select-none">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={`min-w-[32px] h-8 px-1 rounded-lg text-[12px] font-bold transition-colors flex-shrink-0 ${p === page
                  ? 'bg-navy text-white'
                  : 'text-gray-600 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          aria-label="Next page"
          className={navBtn}
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-[12px]" />
        </button>
      </div>
    </div>
  );
}
